let curveRowSequence = 0;

export const DEFAULT_WIND_MODELING = {
  windSpeedModel: "hellman",
  temperatureModel: "linear_gradient",
  densityModel: "ideal_gas",
  powerOutputModel: "power_curve",
  densityCorrection: true,
  hellmanExponent: 0.14,
  obstacleHeight: 0,
  roughnessLength: 0.15,
  windMeasurementHeight: 10,
  temperatureMeasurementHeight: 2,
  upperWindHeight: 80,
  pressureMode: "measured_or_elevation_fallback",
  fixedPressurePa: 101325,
  // Farm-level modeling (windpowerlib TurbineClusterModelChain).
  // Mirrors DEFAULT_WIND_MODELING in the backend (api/services/wind_generation_service.py).
  wakeLossesModel: "dena_mean",
  powerCurveSmoothing: false,
  standardDeviationMethod: "turbulence_intensity",
  smoothingOrder: "wind_farm_power_curves",
  turbulenceIntensity: "", // empty => derived from roughness length (TI = 1 / ln(h_hub / z0))
};

export const DEFAULT_WIND = {
  enabled: false,
  read_turbine: "",
  turbineCount: 1,
  hubHeight: 100,
  farmEfficiency: 100,
  useProjectLocation: true,
  loc: { lat: "", lon: "" },
  modeling: { ...DEFAULT_WIND_MODELING },
};

export const WIND_SPEED_MODEL_OPTIONS = [
  { value: "hellman", label: "Hellman" },
  { value: "logarithmic", label: "Logarithmic" },
  { value: "interpolation_extrapolation", label: "Interpolation / extrapolation" },
  { value: "log_interpolation_extrapolation", label: "Log interpolation / extrapolation" },
];

export const TEMPERATURE_MODEL_OPTIONS = [
  { value: "linear_gradient", label: "Linear gradient" },
  { value: "interpolation_extrapolation", label: "Interpolation / extrapolation" },
];

export const DENSITY_MODEL_OPTIONS = [
  { value: "ideal_gas", label: "Ideal gas" },
  { value: "barometric", label: "Barometric" },
  { value: "interpolation_extrapolation", label: "Interpolation / extrapolation" },
];

export const POWER_OUTPUT_MODEL_OPTIONS = [
  { value: "power_curve", label: "Power curve" },
  { value: "power_coefficient_curve", label: "Power coefficient curve" },
];

export const PRESSURE_MODE_OPTIONS = [
  { value: "measured_or_elevation_fallback", label: "Measured or elevation fallback" },
  { value: "measured_only", label: "Measured only" },
  { value: "fixed", label: "Fixed value" },
];

// Values must match WAKE_LOSSES_MODELS in api/services/wind_generation_service.py.
export const WAKE_LOSSES_MODEL_OPTIONS = [
  { value: "dena_mean", label: "Wind efficiency curve - dena mean" },
  { value: "knorr_mean", label: "Wind efficiency curve - Knorr mean" },
  { value: "dena_extreme1", label: "Wind efficiency curve - dena extreme 1" },
  { value: "dena_extreme2", label: "Wind efficiency curve - dena extreme 2" },
  { value: "knorr_extreme1", label: "Wind efficiency curve - Knorr extreme 1" },
  { value: "knorr_extreme2", label: "Wind efficiency curve - Knorr extreme 2" },
  { value: "knorr_extreme3", label: "Wind efficiency curve - Knorr extreme 3" },
  { value: "wind_farm_efficiency", label: "Constant farm efficiency" },
  { value: "none", label: "None (no wake losses)" },
];

// Values must match STANDARD_DEVIATION_METHODS in api/services/wind_generation_service.py.
export const STANDARD_DEVIATION_METHOD_OPTIONS = [
  { value: "turbulence_intensity", label: "Turbulence intensity" },
  { value: "Staffell_Pfenninger", label: "Staffell-Pfenninger (empirical)" },
];

// Values must match SMOOTHING_ORDERS in api/services/wind_generation_service.py.
export const SMOOTHING_ORDER_OPTIONS = [
  { value: "wind_farm_power_curves", label: "Farm power curve (after aggregation)" },
  { value: "turbine_power_curves", label: "Turbine power curves (before aggregation)" },
];

export const CURVE_DEFINITIONS = {
  power_curve: {
    key: "power_curve",
    label: "Power curve",
    xLabel: "Wind speed",
    xUnit: "m/s",
    yLabel: "Power",
    yUnit: "W",
    minPoints: 2,
    minY: 0,
  },
  power_coefficient_curve: {
    key: "power_coefficient_curve",
    label: "Power coefficient curve",
    xLabel: "Wind speed",
    xUnit: "m/s",
    yLabel: "Cp",
    yUnit: "",
    minPoints: 2,
    minY: 0,
  },
};

export function setPathInObject(obj, path, value) {
  const keys = path.split(".");
  const next = { ...obj };
  let cursor = next;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
    } else {
      cursor[key] = { ...(cursor[key] || {}) };
      cursor = cursor[key];
    }
  });
  return next;
}

export function mergeWindConfig(rawWind = {}) {
  return {
    ...DEFAULT_WIND,
    ...(rawWind || {}),
    loc: {
      ...DEFAULT_WIND.loc,
      ...(rawWind?.loc || {}),
    },
    modeling: {
      ...DEFAULT_WIND_MODELING,
      ...(rawWind?.modeling || {}),
    },
  };
}

export function isWindModelingModified(modeling) {
  const normalized = {
    ...DEFAULT_WIND_MODELING,
    ...(modeling || {}),
  };
  return JSON.stringify(normalized) !== JSON.stringify(DEFAULT_WIND_MODELING);
}

export function createCurveRow(values = {}) {
  curveRowSequence += 1;
  return {
    id: values.id || `curve-row-${curveRowSequence}`,
    x: values.x == null ? "" : String(values.x),
    y: values.y == null ? "" : String(values.y),
  };
}

export function ensureCurveRows(rows, minimumRows = 2) {
  const nextRows = Array.isArray(rows)
    ? rows.map((row) => createCurveRow(row))
    : [];
  while (nextRows.length < minimumRows) {
    nextRows.push(createCurveRow());
  }
  return nextRows;
}

export function parseCurveImportText(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const parsedRows = [];
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const usesDelimitedColumns = /[\t,;]+/.test(line);
    const parts = line
      .split(usesDelimitedColumns ? /[\t,;]+/ : /\s+/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length !== 2) {
      if (index === 0) {
        const looseParts = line
          .split(/[\t,;]+/)
          .map((part) => part.trim())
          .filter(Boolean);
        if (
          looseParts.length === 2 &&
          Number.isNaN(Number(looseParts[0])) &&
          Number.isNaN(Number(looseParts[1]))
        ) {
          continue;
        }
      }
      return {
        error: `Line ${index + 1} must contain exactly two columns.`,
      };
    }

    parsedRows.push(createCurveRow({ x: parts[0], y: parts[1] }));
  }

  if (parsedRows.length === 0) {
    return { error: "No curve points were found in the imported data." };
  }

  return { rows: parsedRows };
}

export function sortCurveRows(rows) {
  const sortable = [];
  const unsortable = [];

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const x = Number(row?.x);
    if (row?.x !== "" && Number.isFinite(x)) {
      sortable.push({ ...row, _numericX: x });
    } else {
      unsortable.push(createCurveRow(row));
    }
  });

  sortable.sort((a, b) => a._numericX - b._numericX);
  return [
    ...sortable.map(({ _numericX, ...row }) => createCurveRow(row)),
    ...unsortable,
  ];
}

export function validateCurveRows(rows, definition = CURVE_DEFINITIONS.power_curve) {
  const rowErrors = {};
  const formErrors = [];
  const parsedPoints = [];

  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const hasAnyValue = row?.x !== "" || row?.y !== "";
    if (!hasAnyValue) return;

    const errors = {};
    const x = Number(row?.x);
    const y = Number(row?.y);

    if (row?.x === "") {
      errors.x = `${definition.xLabel} is required.`;
    } else if (!Number.isFinite(x)) {
      errors.x = `${definition.xLabel} must be numeric.`;
    }

    if (row?.y === "") {
      errors.y = `${definition.yLabel} is required.`;
    } else if (!Number.isFinite(y)) {
      errors.y = `${definition.yLabel} must be numeric.`;
    } else if (definition.minY != null && y < definition.minY) {
      errors.y = `${definition.yLabel} must be at least ${definition.minY}.`;
    }

    if (Object.keys(errors).length > 0) {
      rowErrors[row.id || `row-${index}`] = errors;
      return;
    }

    parsedPoints.push({
      id: row.id || `row-${index}`,
      x,
      y,
      index,
    });
  });

  if (parsedPoints.length < (definition.minPoints || 2)) {
    formErrors.push(
      `${definition.label} requires at least ${definition.minPoints || 2} valid points.`
    );
  }

  for (let i = 1; i < parsedPoints.length; i += 1) {
    if (parsedPoints[i].x <= parsedPoints[i - 1].x) {
      rowErrors[parsedPoints[i].id] = {
        ...(rowErrors[parsedPoints[i].id] || {}),
        x: `${definition.xLabel} values must be strictly increasing.`,
      };
    }
  }

  return {
    rowErrors,
    formErrors,
    parsedPoints,
    pointCount: parsedPoints.length,
    hasErrors:
      formErrors.length > 0 || Object.keys(rowErrors).some((key) => Object.keys(rowErrors[key]).length > 0),
  };
}

export function curveRowsToSeries(rows, definition = CURVE_DEFINITIONS.power_curve) {
  const validation = validateCurveRows(rows, definition);
  return validation.parsedPoints.map((point) => ({
    x: point.x,
    y: point.y,
  }));
}

export function formatEngineeringNumber(value, maxFractionDigits = 2) {
  if (value == null || value === "") return "-";
  if (typeof value !== "number") {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return String(value);
    return numeric.toLocaleString("en-GB", { maximumFractionDigits: maxFractionDigits });
  }
  return value.toLocaleString("en-GB", { maximumFractionDigits: maxFractionDigits });
}
